import React from 'react';
import { CapabilityNoetivraNote } from './CapabilityNoetivraNote';
import { motion } from 'framer-motion';
import { Brain, Target, CheckCircle, XCircle } from 'lucide-react';
export function ReasoningQualityPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1218" data-mp="9wxvhj79mw78">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1219" data-mp="5wdwsb71nkiz">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1220" data-mp="3ebnvrafn09mj">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1221" data-mp="3ieaqbj3p08nf">
            <span className="w-8 h-px bg-teal" data-id="element-1222" data-mp="3iby06ins6rza"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1223" data-mp="2unoi9rtouodf">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1224" data-mp="2qmmhv7o1c69v"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1225" data-mp="5vxu21zjcrl7">
            Reasoning Quality
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1226" data-mp="fmsf5311pl52">
            At SyNeural, reasoning quality refers to the ability to evaluate
            claims clearly, weigh evidence appropriately, recognize
            contradictions, distinguish strong arguments from weak ones, and
            process complexity without collapsing into confusion, impulse, or
            oversimplification.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1227" data-mp="3t41i9ed7db6m">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1228" data-mp="1wozww0l2rp4">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1229" data-mp="39fs4aqlzse5n">
            It is one of the most foundational human capabilities because it
            shapes how people interpret information, form conclusions, and make
            decisions across all areas of life.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1230" data-mp="3edjv1hawhu89">
            Reasoning quality is not just a measure of intelligence or
            education. A person may be knowledgeable, verbal, or credentialed
            and still reason poorly under pressure, emotion, ideology, or social
            influence. Strong reasoning involves clarity, structure, evidence
            sensitivity, and the ability to remain grounded when facing
            uncertainty, disagreement, or competing narratives.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1231" data-mp="22x3okdhbqnxj">
        <div className="max-w-4xl mx-auto" data-id="element-1232" data-mp="1sswsgpyaqhqz">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1233" data-mp="1xkes9aol2cad">
            <div className="flex items-center gap-4 mb-6" data-id="element-1234" data-mp="2bl7ktpvbqxau">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1235" data-mp="2gj98yp7lxmt6">
                <Target size={24} data-id="element-1236" data-mp="21m8mp186qk23" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1237" data-mp="21lqklnyudbm9">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1238" data-mp="q7374wmygr12">
              Reasoning quality affects nearly everything else in the SyNeural
              framework. It influences how people judge risk, interpret events,
              respond to persuasion, handle disagreement, and decide what is
              true, useful, or responsible. Weak reasoning can lead to
              confusion, poor judgment, narrative capture, emotional
              overreaction, and avoidable mistakes. Strong reasoning supports
              better decisions, greater independence of thought, and a more
              reliable foundation for growth and responsibility.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Reasoning Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1239" data-mp="1pazjx6atw6vr">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1240" data-mp="1kc661crmqdv2">
          <div className="flex items-center gap-4 mb-6" data-id="element-1241" data-mp="41lnxcduebcx">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1242" data-mp="2fnvvjb0bbugs">
              <CheckCircle size={24} data-id="element-1243" data-mp="2kmz06ucdryo8" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1244" data-mp="2pl0jngdpy008">
              What Strong Reasoning Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1245" data-mp="2ucile6nkg4m9">
            Strong reasoning involves asking whether a claim is supported,
            whether evidence is strong or weak, whether assumptions are being
            smuggled in, and whether conclusions actually follow from the facts
            presented. It also includes the ability to tolerate complexity
            without reaching for simplistic answers too quickly. People with
            stronger reasoning quality are better able to track nuance,
            recognize when they do not know enough, and update their views when
            better information appears.
          </p>
        </motion.div>
      </section>

      {/* What Weak Reasoning Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1246" data-mp="1axp9kxk4u0oh">
        <div className="max-w-4xl mx-auto" data-id="element-1247" data-mp="16wdienlm17e6">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1248" data-mp="1kulcmnr9huln">
            <div className="flex items-center gap-4 mb-6" data-id="element-1249" data-mp="1pswk4ru33t0p">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1250" data-mp="23kt7kikt5eqr">
                <XCircle size={24} data-id="element-1251" data-mp="1ymhywb691aa4" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1252" data-mp="2diqvf4yr99iu">
                What Weak Reasoning Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1253" data-mp="28l7jibpj58u2">
              Weak reasoning often shows up as contradiction blindness,
              emotional substitution for evidence, simplistic thinking, poor
              causal understanding, and overconfidence in shaky conclusions. It
              may also appear as a tendency to accept arguments because they
              feel satisfying, socially convenient, or identity-reinforcing
              rather than because they are well-supported. In practical terms,
              weak reasoning reduces reliability in learning, civic judgment,
              work, and leadership.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1254" data-mp="24fls7i2ejeip">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1255" data-mp="24dhfcamiz56a">
          <div className="flex items-center gap-4 mb-6" data-id="element-1256" data-mp="25y3x0pz7ub5g">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1257" data-mp="25ylqxfyeoda7">
              <Brain size={24} data-id="element-1258" data-mp="20x65hmsbg4ra" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1259" data-mp="1w0fpezgih95v">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1260" data-mp="1r24f6nmb0wn4">
            Reasoning quality can be improved through logic training, argument
            analysis, better reading habits, evidence evaluation, structured
            debate, exposure to complexity, and repeated practice in
            distinguishing signal from noise. It also improves when people learn
            to slow down, question assumptions, and separate emotional reaction
            from analytical judgment. In the SyNeural framework, reasoning
            quality is treated not as a fixed trait, but as a capability that
            can be developed over time.
          </p>
        </motion.div>
      </section>
      <CapabilityNoetivraNote capability="Reasoning quality" data-id="element-1261" data-mp="y4twtzse1fvz" />
    </div>;
}